import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchReferralDashboard, ensureReferralCode } from "../lib/api";

/**
 * ContractorReferrals — share your code, see who you've referred, track $$.
 *
 * Layout:
 *   Hero stat tiles (referred / paid / cash earned / free months)
 *   Big share card with code + copy-link + share-by-text/email
 *   Two tables: People you've referred · Cash payouts
 */
const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const GREEN = "#1A7A4A";
const RED = "#A32D2D";
const GREY = "#5a5d66";
const BG = "#F8F9FA";

const dollars = (cents) => `$${(((cents || 0) / 100) || 0).toFixed(0)}`;
const formatDate = (s) => s ? new Date(s).toLocaleDateString() : "—";

const ContractorReferrals = () => {
  const { contractorId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      await ensureReferralCode(contractorId);
      const d = await fetchReferralDashboard(contractorId);
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't load referrals.");
    }
  };

  useEffect(() => { if (contractorId) load(); }, [contractorId]);

  const copyLink = () => {
    if (!data?.share_url) return;
    navigator.clipboard?.writeText(data.share_url);
    alert(`Copied:\n${data.share_url}`);
  };
  const shareSMS = () => {
    const m = encodeURIComponent(data?.share_message || "");
    window.location.href = `sms:?&body=${m}`;
  };
  const shareEmail = () => {
    const subj = encodeURIComponent("You should try NearNow.biz");
    const body = encodeURIComponent(data?.share_message || "");
    window.location.href = `mailto:?subject=${subj}&body=${body}`;
  };

  if (err) return <ErrorState err={err} />;
  if (!data) return <LoadingState />;

  return (
    <div data-testid="contractor-referrals-page" style={{ minHeight: "100vh", background: BG, padding: "32px 22px 80px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <p style={{ color: GOLD, letterSpacing: 4, fontSize: 13, fontFamily: "Bebas Neue, sans-serif", margin: 0 }}>
          NEARNOW · REFERRAL PROGRAM
        </p>
        <h1 className="font-display" style={{ fontSize: 56, color: NAVY, letterSpacing: 1, margin: "6px 0 4px", lineHeight: 1.04 }}>
          REFER. EARN. REPEAT.
        </h1>
        <p style={{ color: NAVY, fontSize: 17, margin: 0 }}>
          $50 cash + 1 free month for every paid contractor you refer. No cap. No expiration.
        </p>

        {/* Stat tiles */}
        <div data-testid="referral-stats" style={{ marginTop: 24, display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
          <Stat label="PEOPLE REFERRED" value={data.referred_count} />
          <Stat label="PAID SIGNUPS" value={data.paid_count} color={GREEN} />
          <Stat label="CASH EARNED" value={dollars(data.cash_earned_cents)} color={GREEN} />
          <Stat label="OWED TO YOU" value={dollars(data.cash_owed_cents)} color={RED} />
          <Stat label="FREE MONTHS" value={data.free_months_earned} color={GOLD} />
        </div>

        {/* Share card */}
        <div data-testid="share-card" style={{ marginTop: 28, background: NAVY, color: "#fff",
          borderRadius: 16, padding: 28, border: `2px solid ${GOLD}` }}>
          <p style={{ color: GOLD, letterSpacing: 3, fontSize: 13, fontFamily: "Bebas Neue, sans-serif", margin: 0 }}>
            YOUR REFERRAL LINK
          </p>
          <div className="font-display" data-testid="referral-code-display"
            style={{ fontSize: 64, lineHeight: 1, color: GOLD, letterSpacing: 4, marginTop: 6 }}>
            {data.referral_code}
          </div>
          <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <code style={{ background: "rgba(255,255,255,0.08)", padding: "8px 12px", borderRadius: 6,
              fontSize: 15, color: "#cbd0db" }}>{data.share_url}</code>
            <button data-testid="copy-link-btn" onClick={copyLink} className="btn-gold tap"
              style={{ minHeight: 40, fontSize: 13, padding: "0 14px" }}>COPY</button>
          </div>
          <p style={{ color: "#cbd0db", fontSize: 14, marginTop: 14 }}>
            Send this to any RI contractor. When they subscribe, you get <strong style={{ color: GOLD }}>$50 cash + 1 free month</strong> — auto-credited to your account.
          </p>
          <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
            <button data-testid="share-sms-btn" onClick={shareSMS} className="btn-gold tap"
              style={{ minHeight: 52, fontSize: 15, padding: "0 18px", letterSpacing: 1 }}>
              📱 SHARE BY TEXT
            </button>
            <button data-testid="share-email-btn" onClick={shareEmail} className="btn-outline-gold tap"
              style={{ minHeight: 52, fontSize: 15, padding: "0 18px", letterSpacing: 1,
                        border: `2px solid ${GOLD}`, background: "transparent", color: GOLD,
                        fontFamily: "Bebas Neue, sans-serif", borderRadius: 8, cursor: "pointer" }}>
              ✉️ SHARE BY EMAIL
            </button>
          </div>
        </div>

        {/* Referred list */}
        <Section title="PEOPLE YOU'VE REFERRED">
          {(data.referred || []).length === 0 ? (
            <Empty msg="Share your link to start earning. Every paid signup = $50 + 1 free month." />
          ) : (
            <table data-testid="referred-table" style={tableSty}>
              <thead><tr style={{ background: BG }}>
                <Th>CONTRACTOR</Th><Th>TRADE · CITY</Th><Th>PLAN</Th>
                <Th>STATUS</Th><Th align="right">SIGNED UP</Th>
              </tr></thead>
              <tbody>
                {data.referred.map((c) => (
                  <tr key={c.id} data-testid={`referred-row-${c.id}`} style={{ borderTop: `1px solid ${BG}` }}>
                    <td style={cell}>
                      <strong style={{ color: NAVY }}>{c.business_name || c.owner_name || "—"}</strong>
                    </td>
                    <td style={cell}>{c.trade || "—"} · {c.city || "—"}</td>
                    <td style={cell}>{(c.plan || "—").toUpperCase()}</td>
                    <td style={cell}>
                      {c.stripe_cc_on_file
                        ? <span style={{ color: GREEN, fontWeight: 700 }}>✓ PAID — $50 earned</span>
                        : <span style={{ color: GREY }}>pipeline</span>}
                    </td>
                    <td style={{ ...cell, textAlign: "right" }}>{formatDate(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        {/* Payout history */}
        <Section title="CASH PAYOUTS">
          {(data.credits || []).length === 0 ? (
            <Empty msg="No payouts yet." />
          ) : (
            <table style={tableSty}>
              <thead><tr style={{ background: BG }}>
                <Th>FROM</Th><Th align="right">AMOUNT</Th><Th>FREE MONTH</Th>
                <Th>STATUS</Th><Th align="right">EARNED</Th><Th align="right">PAID</Th>
              </tr></thead>
              <tbody>
                {data.credits.map((x) => (
                  <tr key={x.id} style={{ borderTop: `1px solid ${BG}` }}>
                    <td style={cell}>{x.referred_business || "—"}</td>
                    <td style={{ ...cell, textAlign: "right", color: GREEN, fontWeight: 700 }}>
                      {dollars(x.cash_payout_cents)}
                    </td>
                    <td style={cell}>
                      {x.stripe_credit_applied
                        ? <span style={{ color: GREEN }}>✓ APPLIED</span>
                        : <span style={{ color: GREY }}>{x.stripe_credit_error || "pending"}</span>}
                    </td>
                    <td style={cell}>
                      {x.payout_status === "paid"
                        ? <span style={{ color: GREEN, fontWeight: 700 }}>PAID via {x.payout_method}</span>
                        : <span style={{ color: RED, fontWeight: 700 }}>OWED</span>}
                    </td>
                    <td style={{ ...cell, textAlign: "right" }}>{formatDate(x.created_at)}</td>
                    <td style={{ ...cell, textAlign: "right" }}>{formatDate(x.payout_paid_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        <p style={{ marginTop: 30, textAlign: "center", color: GREY, fontSize: 13 }}>
          Questions? <Link to="/" style={{ color: NAVY, textDecoration: "underline" }}>Contact NearNow</Link>
        </p>
      </div>
    </div>
  );
};

const LoadingState = () => (
  <div style={{ padding: 80, textAlign: "center", color: GREY }}>Loading your referrals…</div>
);
const ErrorState = ({ err }) => (
  <div style={{ padding: 80, textAlign: "center", color: RED }}>
    Couldn&apos;t load: {String(err)}<br/>
    <Link to="/" style={{ color: NAVY }}>← Back home</Link>
  </div>
);

const Stat = ({ label, value, color }) => (
  <div data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`} style={{ background: "#fff", padding: 16, borderRadius: 12 }}>
    <div style={{ color: GREY, letterSpacing: 2, fontSize: 11, fontFamily: "Bebas Neue, sans-serif" }}>{label}</div>
    <div className="font-display" style={{ fontSize: 38, color: color || NAVY, marginTop: 4, lineHeight: 1 }}>{value}</div>
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginTop: 30 }}>
    <p style={{ color: GOLD, letterSpacing: 3, fontSize: 13, fontFamily: "Bebas Neue, sans-serif", margin: "0 0 10px" }}>{title}</p>
    {children}
  </div>
);

const Empty = ({ msg }) => (
  <div style={{ background: "#fff", borderRadius: 10, padding: 30, textAlign: "center", color: GREY }}>{msg}</div>
);

const tableSty = { width: "100%", borderCollapse: "collapse", fontSize: 14, background: "#fff", borderRadius: 10, overflow: "hidden" };
const Th = ({ children, align }) => (
  <th style={{ textAlign: align || "left", padding: "12px 14px", fontSize: 11,
    color: GREY, letterSpacing: 1.5, fontFamily: "Bebas Neue, sans-serif" }}>{children}</th>
);
const cell = { padding: "14px" };

export default ContractorReferrals;
